import { defineStore } from 'pinia';
import KontaTelephonyClient from 'dashboard/api/channel/kontaVoice/kontaTelephonyClient';
import { KONTA_TERMINAL_STATUSES } from 'dashboard/helper/kontaVoice';

// A call matches by its sid, or by conversationId when the sid drifted (pending→real backfill,
// agent-vs-contact leg). Shared by the status-change and answered handlers.
const matchesCallRef = (call, callSid, conversationId) => {
  const convId = conversationId != null ? Number(conversationId) : null;
  return (
    call.callSid === callSid ||
    (convId != null && Number(call.conversationId) === convId)
  );
};

export const useKontaCallsStore = defineStore('kontaCalls', {
  state: () => ({
    calls: [],
  }),

  getters: {
    activeCall: state => state.calls.find(call => call.isActive) || null,
    hasActiveCall: state => state.calls.some(call => call.isActive),
    incomingCalls: state => state.calls.filter(call => !call.isActive),
    hasIncomingCall: state => state.calls.some(call => !call.isActive),
  },

  actions: {
    handleCallStatusChanged({ callSid, status, conversationId }) {
      if (!KONTA_TERMINAL_STATUSES.includes(status)) return;

      // Remove by callSid OR conversationId so sid drift (pending→real sid
      // backfill, agent-vs-contact leg) doesn't leave the call stuck.
      const toRemove = this.calls.filter(c =>
        matchesCallRef(c, callSid, conversationId)
      );
      const wasActive = toRemove.some(c => c.isActive);
      if (wasActive) KontaTelephonyClient.endClientCall();
      const removeSet = new Set(toRemove.map(c => c.callSid));
      this.calls = this.calls.filter(c => !removeSet.has(c.callSid));
    },

    handleCallAnswered({
      callSid,
      conversationId,
      acceptedByAgentId,
      currentUserId,
    }) {
      // Someone claimed the call: drop the incoming banner everywhere except the window that
      // actually answered. That window is the one whose agent id matches acceptedByAgentId — a
      // deterministic check that survives the broadcast racing ahead of the local isActive flip
      // (or a rollback). Fall back to isActive only when the ids are not available to compare.
      const acceptedByMe =
        acceptedByAgentId != null &&
        currentUserId != null &&
        Number(acceptedByAgentId) === Number(currentUserId);
      this.calls = this.calls.filter(
        call =>
          !matchesCallRef(call, callSid, conversationId) ||
          call.isActive ||
          acceptedByMe
      );
    },

    addCall(callData) {
      if (!callData?.callSid) return;
      const existing = this.calls.find(
        call => call.callSid === callData.callSid
      );
      if (existing) {
        // Outbound adds the call from both the API response and the ActionCable push (same
        // callSid); backfill the contact name from whichever source has it.
        if (callData.contactName && !existing.contactName) {
          existing.contactName = callData.contactName;
        }
        return;
      }

      this.calls.push({
        ...callData,
        isActive: false,
      });
    },

    removeCall(callSid) {
      const callToRemove = this.calls.find(c => c.callSid === callSid);
      if (callToRemove?.isActive) {
        KontaTelephonyClient.endClientCall();
      }
      this.calls = this.calls.filter(c => c.callSid !== callSid);
    },

    setCallActive(callSid) {
      this.calls = this.calls.map(call => ({
        ...call,
        isActive: call.callSid === callSid,
      }));
    },

    rollbackActivation(callSid, previousActiveSid) {
      // Undo the optimistic activation in joinCall: the failed call returns to its incoming-banner
      // state and the call that was active before (if any) is restored, so an agent's ongoing call
      // is never left orphaned when they click a second banner.
      this.calls = this.calls.map(call => ({
        ...call,
        isActive:
          previousActiveSid != null && call.callSid === previousActiveSid,
      }));
    },

    clearActiveCall() {
      KontaTelephonyClient.endClientCall();
      this.calls = this.calls.filter(call => !call.isActive);
    },

    dismissCall(callSid) {
      this.calls = this.calls.filter(call => call.callSid !== callSid);
    },
  },
});
